
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Eye, FileText, FilePen, FileX, Download, UserRound } from "lucide-react";
import { ViewPatientFileDialog } from "./ViewPatientFileDialog";
import { EditPatientFileDialog } from "./EditPatientFileDialog";
import { DeletePatientFileDialog } from "./DeletePatientFileDialog";
import { format } from "date-fns";
import { usePatientFileExport } from "./usePatientFileExport";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";

interface PatientFileRowProps {
  file: any;
}

export const PatientFileRow = ({ file }: PatientFileRowProps) => {
  const [viewOpen, setViewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { exportToPDF, exportToCSV, isExporting } = usePatientFileExport();

  // Formater les valeurs pour l'affichage
  const formatAge = () => {
    if (!file.birth_date) return '';
    const birthDate = new Date(file.birth_date);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return `${age} ans`;
  };

  return (
    <>
      <Card className="p-4 hover:bg-accent/5 transition-colors border-l-4 border-l-primary shadow-sm">
        <div className="flex justify-between items-center">
          <div className="flex flex-col flex-1">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <UserRound className="h-5 w-5 text-primary" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-lg">{file.last_name} {file.first_name}</span>
                  <Badge variant="outline" className="text-xs">{file.file_number}</Badge>
                  {file.gender && (
                    <Badge variant={file.gender === 'M' ? 'default' : 'secondary'} className="text-xs">
                      {file.gender === 'M' ? 'Homme' : file.gender === 'F' ? 'Femme' : file.gender}
                    </Badge>
                  )}
                </div>
                <div className="flex gap-2 text-sm text-muted-foreground mt-1">
                  {file.birth_date && (
                    <span className="flex items-center">
                      <span className="mr-1">Né(e) le {format(new Date(file.birth_date), "dd/MM/yyyy")}</span>
                      {formatAge() && <span className="text-xs px-1.5 py-0.5 bg-muted rounded">{formatAge()}</span>}
                    </span>
                  )}
                  {file.email && <span>• {file.email}</span>}
                  {file.phone_number && <span>• {file.phone_number}</span>}
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" onClick={() => setViewOpen(true)} className="text-blue-500 hover:text-blue-700 hover:bg-blue-50">
              <Eye className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setEditOpen(true)} className="text-amber-500 hover:text-amber-700 hover:bg-amber-50">
              <FilePen className="h-4 w-4" />
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" disabled={isExporting} className="text-green-500 hover:text-green-700 hover:bg-green-50">
                  <Download className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={() => exportToCSV(file)}>
                  Exporter en CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportToPDF(file, 'standard')}>
                  PDF - Format Standard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportToPDF(file, 'detailed')}>
                  PDF - Format Détaillé
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportToPDF(file, 'compact')}>
                  PDF - Format Compact
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Button variant="ghost" size="icon" onClick={() => setDeleteOpen(true)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
              <FileX className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>

      <ViewPatientFileDialog
        open={viewOpen}
        onOpenChange={setViewOpen}
        file={file}
      />
      
      <EditPatientFileDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        file={file}
      />
      
      <DeletePatientFileDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        file={file}
      />
    </>
  );
};
